import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cn } from '@shared/lib';
import { buildLookupView, groupByPos, isLocked } from '../lib/senseChoice';
import type { LookupState } from '../lib/useTranslationLookup';

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h13M12 5l7 7-7 7" />
    </svg>
  );
}

/**
 * Часть речи в сокращении на текущем языке (сущ./гл./…) для компактного превью.
 * Фолбэк: полное название → сырой pos из словаря.
 */
function posShort(t: TFunction, pos: string | null): string {
  if (!pos) return t('cardsWorkspace.lookup.posShort.other');
  const key = pos.toLowerCase();
  return t(`cardsWorkspace.lookup.posShort.${key}`, {
    defaultValue: t(`cardsWorkspace.lookup.pos.${key}`, { defaultValue: pos }),
  });
}

export interface CompactLookupProps {
  state: LookupState;
  onChoose: () => void;
  onManual: () => void;
  /** Причина ошибки поиска по существу (главная, ТЗ главной § 10); без неё — общий текст `/cards/new`. */
  errorText?: string;
  /** «Повторить» у ошибки (главная); без него кнопки нет — как на `/cards/new`. */
  onRetry?: () => void;
  /** Строка под счётчиками разделов (главная: «Жетоны закончились…», ТЗ § 7). */
  note?: ReactNode;
  /**
   * Предел высоты: превью прокручивается само, слово сверху и плашка «Выбрать, что учить» снизу
   * остаются видны (главная, телефон боком с клавиатурой). Без него прокручивает родитель (`/cards/new`).
   */
  maxHeight?: number;
  /**
   * Короткие ответы («Перевод не найден») на непрозрачной карточке `bg-card`, как найденное превью: на главной
   * превью лежит над затемнённым контентом, и сквозь прозрачный тёплый фон читались строки модулей (приёмка
   * A1-03). На `/cards/new` превью стоит на фоне страницы — там без него, как раньше.
   */
  solid?: boolean;
}

/**
 * Шаг 1 телефона (макет «Создание карточек мобайл v2»): слово с транскрипцией и по одному главному
 * переводу на часть речи, справа — сколько ещё; счётчики разделов и плашка «Выбрать, что учить»,
 * которая ведёт на шаг 2. Здесь ничего не выбирают. Общий для `/cards/new` (`ComposeBar`) и строки
 * создания на главной (ТЗ главной § 7); загрузку главная показывает кружком в поле, а не этой веткой.
 */
export function CompactLookup({ state, onChoose, onManual, errorText, onRetry, note, maxHeight, solid = false }: CompactLookupProps) {
  const { t } = useTranslation();
  if (state.status === 'idle') return null;

  if (state.status === 'loading') {
    return (
      <div className="flex items-center gap-2 rounded-[12px] border border-line bg-card px-3 py-2.5 text-[14px] text-muted">
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted-2/40 border-t-accent" />
        {t('cardsWorkspace.lookup.searching')}
      </div>
    );
  }

  if (state.status === 'notFound') {
    const row = (
      <div
        className={cn(
          'flex items-center justify-between gap-3 py-1 pl-3 pr-1',
          // Непрозрачная: тёплый оттенок — слоем поверх bg-card карточки.
          solid ? 'rounded-[15px] bg-warn/[0.06]' : 'rounded-[12px] border border-warn/25 bg-warn/[0.06]',
        )}
      >
        <span className="min-w-0 truncate text-[14px] font-medium text-muted">{t('cardsWorkspace.lookup.notFoundTitle')}</span>
        {/* Область нажатия 44 px (было ~28 — меньше правила, ТЗ главной § 7). */}
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onManual}
          className="flex min-h-11 shrink-0 items-center rounded-pill bg-warn/15 px-4 text-[14px] font-semibold text-warn active:bg-warn/25"
        >
          {t('cardsWorkspace.lookup.addManual')}
        </button>
      </div>
    );
    return solid ? <div className="rounded-[16px] border border-line bg-card">{row}</div> : row;
  }

  if (state.status === 'error') {
    return (
      <div className="flex items-center justify-between gap-3 rounded-[12px] border border-line bg-card py-1 pl-3 pr-1 text-[14px] text-muted">
        <span className={cn('min-w-0 py-1.5', !onRetry && 'pr-2')}>{errorText ?? t('cardsWorkspace.lookup.error')}</span>
        {onRetry && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onRetry}
            className="flex min-h-11 shrink-0 items-center rounded-pill px-3 text-[14px] font-medium text-accent-hi active:bg-elev"
          >
            {t('training.retry')}
          </button>
        )}
      </div>
    );
  }

  // found — макет «Создание карточек мобайл v2», шаг 1: главное значение каждой части речи
  // («+N» — сколько ещё), счётчики разделов и плашка перехода к выбору.
  const view = buildLookupView(state.data);
  const preview = view.active.length
    ? groupByPos(view.active)
        .slice(0, 5)
        .map((g) => ({ pos: posShort(t, g.pos), tr: g.items[0].main, more: g.items.length > 1 ? `+${g.items.length - 1}` : '' }))
    : [...view.phrases, ...view.phrasalVerbs]
        .slice(0, 3)
        .map((c) => ({ pos: posShort(t, 'phrase'), tr: `${c.phrase} — ${c.senses[0].main}`, more: '' }));
  const rareWord = view.other.filter((x) => x.frequency === 'rare').length;
  const other = view.other.length - rareWord;
  // Редкие фразы и фразовые глаголы тоже в «Редких» (2026-10-07).
  const rare = rareWord + view.rarePhrases.length;
  const extras = [
    other ? { n: other, label: t('cardsWorkspace.pick.extraOther', { count: other }) } : null,
    rare ? { n: rare, label: t('cardsWorkspace.pick.extraRare', { count: rare }) } : null,
    view.phrases.length ? { n: view.phrases.length, label: t('cardsWorkspace.pick.extraPhrases', { count: view.phrases.length }) } : null,
    view.phrasalVerbs.length
      ? { n: view.phrasalVerbs.length, label: t('cardsWorkspace.pick.extraPhrasalVerbs', { count: view.phrasalVerbs.length }) }
      : null,
  ].filter((x): x is { n: number; label: string } => x !== null);
  const nothingToPick = view.all.length > 0 && view.all.every(isLocked);
  const scroll = maxHeight != null;

  return (
    <div
      className={cn('rounded-[16px] border border-line bg-card', scroll && 'overflow-y-auto overscroll-contain [scrollbar-width:none]')}
      style={scroll ? { maxHeight } : undefined}
    >
      {/* Своя прокрутка (главная): слово прилипает к верху, как плашка — к низу. */}
      <div className={cn('flex items-baseline gap-2 px-4 pb-1.5 pt-3', scroll && 'sticky top-0 z-[1] bg-card')}>
        <span lang="en" className="font-serif text-[20px] font-medium leading-none">
          {state.data.frontName}
        </span>
        {state.data.transcription && <span className="font-serif text-[14px] text-muted">{state.data.transcription}</span>}
      </div>
      <div className="flex flex-col px-1.5">
        {preview.map((row, index) => (
          <div key={`${row.pos}-${index}`} className="grid min-h-[38px] grid-cols-[56px_minmax(0,1fr)_auto] items-center gap-2.5 px-2.5">
            <span className="font-mono text-[12px] text-[#8a8a90]">{row.pos}</span>
            <span className="min-w-0 text-[16px] font-medium leading-[1.3] text-fg">{row.tr}</span>
            <span className="text-[14px] text-[#6a6a70]">{row.more}</span>
          </div>
        ))}
      </div>
      {extras.length > 0 && (
        <div className="mx-1.5 mt-2 flex flex-wrap gap-1.5 border-t border-line px-2.5 pt-2.5">
          {extras.map((e) => (
            <span key={e.label} className="inline-flex h-7 items-baseline gap-1.5 rounded-pill bg-[#26262b] px-2.5 leading-7">
              <span className="text-[14px] font-semibold text-fg">{e.n}</span>
              <span className="text-[14px] text-muted">{e.label}</span>
            </span>
          ))}
        </div>
      )}
      {note && <div className="mx-1.5 mt-2.5 px-2.5">{note}</div>}
      {nothingToPick ? (
        <div className="mx-1.5 mb-1.5 mt-2.5 rounded-[12px] bg-elev px-3 py-2.5 text-[14px] text-muted">
          {t('cardsWorkspace.choose.nothingToPickMobile')}
        </div>
      ) : (
        // Плашка прилипает к низу превью (2026-10-07): с открытой клавиатурой превью ниже
        // карточки, и переход к шагу 2 уходил под обрез прокрутки.
        <div className="sticky bottom-0 mt-1 rounded-b-[15px] bg-card px-1.5 pb-1.5 pt-1.5">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onChoose}
            className="flex w-full items-center gap-3 rounded-[16px] bg-accent/10 py-2.5 pl-4 pr-2.5 text-left shadow-[inset_0_0_0_1px_rgba(243,106,28,0.3)] active:bg-accent/[0.16]"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-semibold text-fg">{t('cardsWorkspace.choose.inviteTitle')}</span>
              <span className="mt-0.5 block text-[14px] text-[#c9c5c0]">{t('cardsWorkspace.pick.inviteSub')}</span>
            </span>
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent text-fg-inv">
              <ArrowIcon className="h-5 w-5" />
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
